import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Download, Check, Pen, AlertCircle, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractSignPage() {
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [language, setLanguage] = useState('de');

  useEffect(() => {
    loadContract();
  }, [contractId]);

  const loadContract = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contracts/${contractId}`);
      setContract(response.data);
    } catch (error) {
      console.error('Error loading contract:', error);
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0A0A0A';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSign = async () => {
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    // Check if signature is empty
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isEmpty = !imageData.data.some((channel, index) => index % 4 !== 3 && channel !== 0);
    
    if (isEmpty) {
      toast.error(language === 'en' ? 'Please sign the contract' : 'Bitte unterschreiben Sie den Vertrag');
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/contracts/${contractId}/sign`, {
        signature: signatureData
      });
      toast.success(language === 'en' ? 'Contract signed successfully!' : 'Vertrag erfolgreich unterschrieben!');
      loadContract(); // Reload to show signed status
      setShowSignature(false);
    } catch (error) {
      toast.error(language === 'en' ? 'Error signing contract' : 'Fehler beim Unterschreiben');
    } finally {
      setSigning(false);
    }
  };

  // Download PDF from backend
  const downloadPDF = () => {
    const pdfUrl = `${API_URL}/api/contracts/${contractId}/pdf?lang=${language}`;
    window.open(pdfUrl, '_blank');
  };

  const downloadContract = () => {
    // Use the new PDF endpoint
    downloadPDF();
  };

  // Handle manual signature upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'en' 
        ? 'Only PDF or image files allowed' 
        : 'Nur PDF- oder Bilddateien erlaubt');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(language === 'en' 
        ? 'File too large (max 10MB)' 
        : 'Datei zu groß (max 10MB)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API_URL}/api/contracts/${contractId}/upload-signed`, formData);

      toast.success(language === 'en' 
        ? 'Signed contract uploaded successfully!' 
        : 'Unterschriebener Vertrag erfolgreich hochgeladen!');
      loadContract(); // Reload to show signed status
    } catch (error) {
      toast.error(language === 'en' 
        ? 'Error uploading contract' 
        : 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F9F9F7]">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <p className="text-xl text-[#666666]">
          {language === 'en' ? 'Contract not found' : 'Vertrag nicht gefunden'}
        </p>
      </div>
    );
  }

  const isSigned = contract.status === 'signed';

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4" data-testid="contract-sign-page">
      <div className="max-w-3xl mx-auto">
        {/* Language Toggle */}
        <div className="flex justify-end mb-4">
          <div className="flex border border-[#E5E5E5] rounded-lg overflow-hidden bg-white">
            <button
              onClick={() => setLanguage('de')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${language === 'de' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
            >
              🇩🇪 DE
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${language === 'en' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
            >
              🇬🇧 EN
            </button>
          </div>
        </div>

        {/* Contract Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6 md:p-8 mb-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
              <FileText className="text-primary" size={24} />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl">
                {language === 'en' ? 'Vehicle Purchase Contract' : 'Kaufvertrag Kraftfahrzeug'}
              </h1>
              <p className="text-[#666666]">
                {contract.car_brand} {contract.car_model} ({contract.car_year})
              </p>
            </div>
          </div>

          {isSigned && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 mb-6">
              <Check className="text-green-600" size={20} />
              <div>
                <p className="font-medium text-green-800">
                  {language === 'en' ? 'Contract Signed' : 'Vertrag unterschrieben'}
                </p>
                <p className="text-sm text-green-600">
                  {language === 'en' ? 'Signed on' : 'Unterschrieben am'}: {new Date(contract.signed_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Contract Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm p-6 md:p-8 mb-6 space-y-6"
        >
          {/* Buyer Info */}
          <div>
            <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
              {language === 'en' ? 'Buyer Information' : 'Käuferinformationen'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><span className="text-[#999999]">{language === 'en' ? 'Name:' : 'Name:'}</span> <span className="font-medium">{contract.buyer_name}</span></div>
              <div><span className="text-[#999999]">{language === 'en' ? 'Email:' : 'E-Mail:'}</span> <span className="font-medium">{contract.buyer_email}</span></div>
              <div className="md:col-span-2"><span className="text-[#999999]">{language === 'en' ? 'Address:' : 'Adresse:'}</span> <span className="font-medium">{contract.buyer_address}</span></div>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="pt-4 border-t border-[#E5E5E5]">
            <h2 className="font-heading text-lg mb-4">
              {language === 'en' ? 'Vehicle Information' : 'Fahrzeuginformationen'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><span className="text-[#999999]">{language === 'en' ? 'Brand/Model:' : 'Marke/Modell:'}</span> <span className="font-medium">{contract.car_brand} {contract.car_model}</span></div>
              <div><span className="text-[#999999]">{language === 'en' ? 'Year:' : 'Baujahr:'}</span> <span className="font-medium">{contract.car_year}</span></div>
              {contract.car_vin && <div><span className="text-[#999999]">{language === 'en' ? 'VIN:' : 'FIN:'}</span> <span className="font-medium">{contract.car_vin}</span></div>}
              {contract.car_mileage && <div><span className="text-[#999999]">{language === 'en' ? 'Mileage:' : 'KM-Stand:'}</span> <span className="font-medium">{contract.car_mileage.toLocaleString()} km</span></div>}
            </div>
          </div>

          {/* Price Info */}
          <div className="pt-4 border-t border-[#E5E5E5]">
            <h2 className="font-heading text-lg mb-4">
              {language === 'en' ? 'Price & Payment' : 'Preis & Zahlung'}
            </h2>
            <div className="bg-gold/5 p-4 rounded-lg">
              <div className="text-3xl font-heading text-primary mb-2">
                €{contract.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
              </div>
              <div className="text-sm text-[#666666]">
                {language === 'en' ? 'Payment:' : 'Zahlung:'} {contract.payment_method}
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="pt-4 border-t border-[#E5E5E5]">
            <h2 className="font-heading text-lg mb-4">
              {language === 'en' ? 'Terms & Conditions' : 'Vertragsbedingungen'}
            </h2>
            <ul className="text-sm space-y-2 text-[#666666]">
              <li>• {language === 'en' ? `Warranty: ${contract.warranty_months} months` : `Garantie: ${contract.warranty_months} Monate`}</li>
              {contract.delivery_date && <li>• {language === 'en' ? `Delivery: ${contract.delivery_date}` : `Lieferung: ${contract.delivery_date}`}</li>}
            </ul>
            {contract.additional_terms && (
              <p className="mt-4 text-sm text-[#666666] whitespace-pre-wrap">
                {language === 'en' ? (contract.additional_terms_en || contract.additional_terms) : contract.additional_terms}
              </p>
            )}
          </div>

          {/* Signature Area */}
          {isSigned && contract.signature_data && (
            <div className="pt-4 border-t border-[#E5E5E5]">
              <h2 className="font-heading text-lg mb-4">
                {language === 'en' ? 'Signature' : 'Unterschrift'}
              </h2>
              <div className="bg-[#F9F9F7] p-4 rounded-lg">
                <img src={contract.signature_data} alt="Signature" className="max-w-[300px]" />
              </div>
            </div>
          )}
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          {!isSigned && (
            <Button
              onClick={() => setShowSignature(true)}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="sign-contract-btn"
            >
              <Pen size={18} />
              {language === 'en' ? 'Sign Digitally' : 'Digital unterschreiben'}
            </Button>
          )}
          <Button
            onClick={downloadContract}
            variant="outline"
            className="flex-1 flex items-center justify-center gap-2"
          >
            <Download size={18} />
            {language === 'en' ? 'Download PDF' : 'PDF herunterladen'}
          </Button>
        </motion.div>

        {/* Manual Signature Upload Section */}
        {!isSigned && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-lg shadow-sm p-6 md:p-8"
          >
            <h3 className="font-heading text-lg mb-2">
              {language === 'en' ? 'Alternative: Upload Signed Contract' : 'Alternativ: Unterschriebenen Vertrag hochladen'}
            </h3>
            <p className="text-sm text-[#666666] mb-4">
              {language === 'en' 
                ? 'If you prefer to sign the contract manually, download the PDF, print it, sign it, scan it, and upload it here.'
                : 'Falls Sie den Vertrag lieber manuell unterschreiben möchten: PDF herunterladen, ausdrucken, unterschreiben, einscannen und hier hochladen.'}
            </p>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="w-full flex items-center justify-center gap-2 border-dashed border-2"
            >
              {uploading ? (
                <div className="animate-spin w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
              ) : (
                <Upload size={18} />
              )}
              {uploading 
                ? (language === 'en' ? 'Uploading...' : 'Wird hochgeladen...') 
                : (language === 'en' ? 'Upload Signed Contract (PDF/Image)' : 'Unterschriebenen Vertrag hochladen (PDF/Bild)')}
            </Button>
            
            <p className="text-xs text-[#999999] mt-2 text-center">
              {language === 'en' ? 'Accepted formats: PDF, PNG, JPG (max. 10MB)' : 'Erlaubte Formate: PDF, PNG, JPG (max. 10MB)'}
            </p>
          </motion.div>
        )}

        {/* Show uploaded signed contract if exists */}
        {contract.signed_document_url && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-green-50 border border-green-200 rounded-lg p-6 mt-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <Check className="text-green-600" size={20} />
              <h3 className="font-medium text-green-800">
                {language === 'en' ? 'Signed Document Uploaded' : 'Unterschriebenes Dokument hochgeladen'}
              </h3>
            </div>
            <a
              href={`${API_URL}${contract.signed_document_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-green-700 hover:text-green-900 underline"
            >
              <Download size={16} />
              {language === 'en' ? 'View/Download Signed Document' : 'Unterschriebenes Dokument ansehen/herunterladen'}
            </a>
          </motion.div>
        )}

        {/* Signature Modal */}
        {showSignature && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg p-6 max-w-lg w-full"
            >
              <h3 className="font-heading text-xl mb-4">
                {language === 'en' ? 'Sign Here' : 'Hier unterschreiben'}
              </h3>
              <p className="text-sm text-[#666666] mb-4">
                {language === 'en' 
                  ? 'Use your mouse or finger to sign below' 
                  : 'Unterschreiben Sie mit der Maus oder dem Finger'}
              </p>
              
              <div className="border-2 border-[#E5E5E5] rounded-lg mb-4 touch-none">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={200}
                  className="w-full bg-white rounded-lg cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={clearSignature}
                  className="flex-1"
                >
                  {language === 'en' ? 'Clear' : 'Löschen'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowSignature(false)}
                  className="flex-1"
                >
                  {language === 'en' ? 'Cancel' : 'Abbrechen'}
                </Button>
                <Button
                  onClick={handleSign}
                  disabled={signing}
                  className="btn-primary flex-1"
                >
                  {signing ? '...' : (language === 'en' ? 'Confirm' : 'Bestätigen')}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
